import * as React from "react"
import { ShadStackedBarChart } from "../ShadStackedBarChart";
import { subDays, startOfToday, endOfToday } from "date-fns";

const chartConfig = {
    desktop: {
      label: "כניסה ראשית",
      color: "hsl(var(--chart-1))",
    },
    mobile: {
      label: "מעבר בן עמי",
      color: "hsl(var(--chart-2))",
    },
    tablet: {
      label: "מעבר ברוך",
      color: "hsl(var(--chart-3))",
    },
    laptop: {
      label: "מעבר קאסם",
      color: "hsl(var(--chart-4))",
    },
    lcdtv: {
      label: "מעבר מסילה",
      color: "hsl(var(--chart-5))",
    },
    ledtv: {
      label: "מעבר ויקי",
      color: "hsl(var(--chart-6))",
    },
  }

  const cardTitle = "סך כל הכניסות למתחם התחנה";
  const cardDescription = "כניסות לפי מיקום";
  const buttonText = "הצג";

  const PRESETS = [
    { label: "שבוע",     days: 7  },
    { label: "14 ימים",  days: 14 },
    { label: "חודש",     days: 30 },
    { label: "3 חודשים", days: 90 },
];

const AtarimStackedBarchart = ({ deviceNames, dataByDuration, updateTimeBasedData }) => {
    const [calenderStartDate, setCalenderStartDate] = React.useState(subDays(startOfToday(), 7));
    const [calenderEndDate, setCalenderEndDate] = React.useState(endOfToday);
    const [calenderComponentDisabled, setCalenderComponentDisabled] = React.useState(false);
    const [activeDays, setActiveDays] = React.useState(7);

    React.useEffect(() => {
        if (calenderComponentDisabled) {
            setCalenderComponentDisabled(false);
        }
    }, [dataByDuration]);

    React.useEffect(() => {
        updateTimeBasedData({
            from: calenderStartDate,
            to: calenderEndDate
        });
    }, []);

    const handleDateSelect = (identifier, date) => {
        if (!date) {
            console.error('Invalid date');
            return;
        }
        setActiveDays(null);
        if (identifier === "start") {
            setCalenderStartDate(date);
        } else if (identifier === "end") {
            setCalenderEndDate(date);
        }
    };

    const handlePreset = (days) => {
        const start = subDays(startOfToday(), days);
        const end = endOfToday();
        setActiveDays(days);
        setCalenderStartDate(start);
        setCalenderEndDate(end);
        setCalenderComponentDisabled(true);
        updateTimeBasedData({ from: start, to: end });
    };

    const presetButtons = PRESETS.map(({ label, days }) => (
        <button
            key={days}
            type="button"
            disabled={calenderComponentDisabled}
            onClick={() => handlePreset(days)}
            style={{
                padding: "3px 10px",
                borderRadius: "9999px",
                fontSize: "11px",
                fontWeight: activeDays === days ? "700" : "500",
                cursor: calenderComponentDisabled ? "not-allowed" : "pointer",
                border: "1px solid",
                borderColor: activeDays === days ? "#3b82f6" : "#e5e7eb",
                background: activeDays === days ? "#eff6ff" : "white",
                color: activeDays === days ? "#1d4ed8" : "#4b5563",
                transition: "all 0.15s ease",
                opacity: calenderComponentDisabled ? 0.6 : 1,
            }}
        >
            {label}
        </button>
    ));

    const result = dataByDuration.map((item, i) => {
        const itemResult = item?.result || [];
        const reduceResult = itemResult.reduce((acc, curr) => {
            const dateOnly = new Date(curr.date).toLocaleDateString("sv-SE", { timeZone: "Asia/Jerusalem" });
            acc[dateOnly] = (acc[dateOnly] || 0) + curr.entering;
            return acc;
        }, {});
        return { [deviceNames[i]]: reduceResult };
    });

    const formattedResult = result.map((resultItem, i) => {
        const locationFormattedResult = Object.entries(resultItem[deviceNames[i]] || {}).map(([date, enteringSum]) => ({
            date,
            enteringSum
        }));
        return locationFormattedResult;
    });

    const deviceTypes = ["desktop", "mobile", "tablet", "laptop", "lcdtv", "ledtv"];
    const chartData = [];

    const allDates = Array.from(
        new Set(formattedResult.flatMap((deviceData) => deviceData.map((d) => d.date)))
    ).sort();

    allDates.forEach((dateKey) => {
        let entry = {
            month: new Date(dateKey).toLocaleString('he-IS', { day: 'numeric', month: 'short', weekday: 'short' })
        };
        formattedResult.forEach((deviceData, deviceIndex) => {
            const match = deviceData.find((d) => d.date === dateKey);
            entry[deviceTypes[deviceIndex]] = match ? match.enteringSum : 0;
        });
        chartData.push(entry);
    });

    const handleDateSubmit = () => {
        setCalenderComponentDisabled(true);
        updateTimeBasedData({
            from: calenderStartDate,
            to: calenderEndDate
        });
    };

    const dataWithTotal = chartData.map(item => ({
        ...item,
        total: deviceTypes.reduce((acc, curr) => ((item[curr] || 0) + acc), 0),
    }));

    return (
        <ShadStackedBarChart
            deviceNames={deviceNames}
            dataByDuration={dataByDuration}
            updateTimeBasedData={updateTimeBasedData}
            handleDateSubmit={handleDateSubmit}
            calenderComponentDisabled={calenderComponentDisabled}
            calenderStartDate={calenderStartDate}
            calenderEndDate={calenderEndDate}
            cardTitle={cardTitle}
            cardDescription={cardDescription}
            buttonText={buttonText}
            presetButtons={presetButtons}
            handleDateSelect={handleDateSelect}
            chartConfig={chartConfig}
            chartData={dataWithTotal}
            deviceTypes={deviceTypes}
            showTotal={true}
        />
    );
};

export default AtarimStackedBarchart;